import * as d3 from "d3";
import { useCallback, useEffect, useRef, useState } from "react";

export const useResizeObserver = (ref) => {
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Set up resize observer to handle container size changes
    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const resizeObserver = new ResizeObserver((entries) => {
            if (!entries || !entries[0]) return;

            const { width, height } = entries[0].contentRect;
            setDimensions({ width, height });
        });
        resizeObserver.observe(element);
        return () => {
            resizeObserver.unobserve(element);
        };
    }, [ref]);

    return dimensions;
};

/**
 * useZoomOnPlot
 *
 * Hook to simplify and extract common behavior when adding
 * zoom behavior to plots.
 */
export const useZoomOnPlot = ({
    svgRef,
    dimensions,
    disableDoubleClickReset = false,
}: {
    svgRef: React.MutableRefObject<SVGSVGElement>;
    dimensions: {
        width: number;
        height: number;
    };
    disableDoubleClickReset?: boolean;
}) => {
    const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform>(
        d3.zoomIdentity,
    );
    const [zoomDomain, setZoomDomain] = useState<{
        x: [number, number] | [Date, Date] | null;
        y: [number, number] | null;
    }>({
        x: null,
        y: null,
    });
    const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown>>();

    // Reset handler
    const resetZoom = useCallback(() => {
        // Reset both zoom transform and domain
        setZoomDomain({ x: null, y: null });
        setCurrentTransform(d3.zoomIdentity);

        if (!svgRef.current || !zoomBehaviorRef.current) return;
        d3.select(svgRef.current).call(
            zoomBehaviorRef.current.transform,
            d3.zoomIdentity,
        );
    }, []);

    // Setup zoom and brush behaviors (runs when dimensions change)
    useEffect(() => {
        if (dimensions.width === 0 || dimensions.height === 0) return;
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);

        // Create zoom behavior (double-click only)
        const zoom = d3
            .zoom<SVGSVGElement, unknown>()
            .on("zoom", (event) => {
                setCurrentTransform(event.transform);
            })
            .filter((event) => {
                // Only allow  double-click, disable drag (drag is for brush)
                return event.type === "dblclick";
            });

        zoomBehaviorRef.current = zoom;

        // Apply zoom to SVG
        svg.call(zoom);

        // Setup double-click to reset (if enabled)
        if (!disableDoubleClickReset) {
            svg.on("dblclick.zoom", () => {
                setZoomDomain({ x: null, y: null });
                setCurrentTransform(d3.zoomIdentity);
                svg.call(zoom.transform, d3.zoomIdentity);
            });
        }
    }, [dimensions, svgRef, disableDoubleClickReset]);

    // Method to add brush handler to plot
    const addZoomBrushHandle = useCallback(
        ({
            width,
            height,
            chart,
            xScale,
            yScale,
            constrainYToZero = true,
        }: {
            width: number;
            height: number;
            chart: any;
            xScale:
                | d3.ScaleTime<number, number>
                | d3.ScaleBand<string>
                | d3.ScaleLinear<number, number>;
            yScale: any;
            constrainYToZero?: boolean;
        }) => {
            // Create brush for box zoom
            const brush = d3
                .brush()
                .extent([
                    [0, 0],
                    [width, height],
                ])
                .on("start brush", (event) => {
                    // Style the selection during brushing
                    if (event.selection) {
                        chart
                            .select(".brush .selection")
                            .attr("fill", "#1677ff")
                            .attr("fill-opacity", 0.2)
                            .attr("stroke", "#1677ff")
                            .attr("stroke-width", 1.5)
                            .attr("stroke-dasharray", "4,4");
                    }
                })
                .on("end", (event) => {
                    if (!event.selection) return;

                    const [[x0, y0], [x1, y1]] = event.selection as [
                        [number, number],
                        [number, number],
                    ];

                    // Ignore very small selections
                    if (Math.abs(x1 - x0) < 10 || Math.abs(y1 - y0) < 10) {
                        chart.select(".brush").call(brush.move, null);
                        return;
                    }

                    // Convert pixel coordinates to data domain using scale inversion
                    // For X: invert scale to get domain values
                    let xDomain: [any, any];
                    if ("bandwidth" in xScale) {
                        // Band scale - convert to indices
                        const domain = xScale.domain();
                        const step = width / domain.length;
                        const startIdx = Math.max(0, Math.floor(x0 / step));
                        const endIdx = Math.min(
                            domain.length,
                            Math.ceil(x1 / step),
                        );
                        xDomain = [startIdx, endIdx];
                    } else {
                        // Continuous scale (time, linear) - use invert directly
                        const continuousScale = xScale as
                            | d3.ScaleTime<number, number>
                            | d3.ScaleLinear<number, number>;
                        xDomain = [
                            continuousScale.invert(x0),
                            continuousScale.invert(x1),
                        ];
                    }

                    // For Y: invert scale (SVG y-axis is inverted)
                    const yDomain: [number, number] = [
                        yScale.invert(y1),
                        yScale.invert(y0),
                    ];

                    // Apply constraint if enabled: always include 0 in Y domain
                    if (constrainYToZero) {
                        yDomain[0] = 0;
                    }

                    setZoomDomain({ x: xDomain, y: yDomain });
                    // Clear brush selection after zoom
                    chart.select(".brush").call(brush.move, null);
                });

            // Attach brush to chart
            chart.append("g").attr("class", "brush").call(brush);
        },
        [],
    );

    return {
        zoomDomain,
        currentTransform,
        addZoomBrushHandle,
        resetZoom,
    };
};
